import React from "react";
import { graphql, useFragment } from "react-relay";

import { faSpinner } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import { TEMPLATE_TITLES } from "./utils";

import type { RecapTemplateType } from "~/__generated__/TranscriptModalGenerateRecapTemplateMutation.graphql";
import type { TranscriptModalGenerating_RedactingPreview_meetingRecording$key } from "~/__generated__/TranscriptModalGenerating_RedactingPreview_meetingRecording.graphql";

import mainStyles from "./css/TranscriptModal.module.css";
import styles from "./css/TranscriptModalGegenerating.module.css";

interface ShimmerProps {
	height?: "extra-thin" | "thin" | "thick";
	width?: string;
	color?: "royal" | "dark" | "light";
	noAnimation?: boolean;
}

function Shimmer({ height, color, width, noAnimation }: ShimmerProps) {
	return (
		<div
			className={cx(styles.shimmer, styles[`shimmer-${color ?? "dark"}`], styles[`shimmer-${height ?? "thin"}`], {
				[styles.shimmerNoAnimation]: noAnimation,
			})}
			style={{ width: `${width ?? "100%"}` }}
		/>
	);
}

function ShimmerBlock({ children }: React.PropsWithChildren<{}>) {
	return <div className={styles.shimmerBlock}>{children}</div>;
}

interface ToggleItemProps {
	active?: boolean;
}

function ToggleItem({ active }: ToggleItemProps) {
	return (
		<div className={cx(styles.loadingToggleItem, { [styles.active]: active })}>
			<Shimmer color={active ? "royal" : "dark"} height="extra-thin" />
		</div>
	);
}

function ToggleGroup() {
	return (
		<div className={cx(mainStyles.leftPanelToggleGroup, styles.loadingToggleGroup)}>
			<ToggleItem active />
			<ToggleItem />
			<ToggleItem />
		</div>
	);
}

function SectionHeader({ width }: Pick<ShimmerProps, "width">) {
	return (
		<div className={styles.sectionHeader}>
			<Shimmer height="thin" width={width} />
			<Shimmer height="thin" width="12px" noAnimation />
		</div>
	);
}

function ActionItem({ width }: Pick<ShimmerProps, "width">) {
	return (
		<div className={styles.actionItem}>
			<Shimmer color="light" height="thin" width="12px" noAnimation />
			<Shimmer color="light" height="thin" width={width} />
		</div>
	);
}

function LeftPanel() {
	return (
		<div className={mainStyles.leftPanel}>
			<ToggleGroup />
			<div className={styles.leftPanelContent}>
				<ShimmerBlock>
					<Shimmer height="thick" width="25%" />
					<Shimmer color="light" width="40%" />
				</ShimmerBlock>
				<ShimmerBlock>
					<SectionHeader width="32%" />
					<Shimmer color="light" width="45%" />
					<Shimmer color="light" width="62%" />
					<Shimmer color="light" width="32%" />
				</ShimmerBlock>
				<ShimmerBlock>
					<SectionHeader width="28%" />
					<ActionItem width="75%" />
					<ActionItem width="53%" />
					<ActionItem width="68%" />
					<ActionItem width="50%" />
				</ShimmerBlock>
				<ShimmerBlock>
					<SectionHeader width="34%" />
					<ActionItem width="54%" />
					<ActionItem width="29%" />
					<ActionItem width="67%" />
				</ShimmerBlock>
				<ShimmerBlock>
					<SectionHeader width="41%" />
					<ActionItem width="27%" />
					<ActionItem width="39%" />
					<ActionItem width="55%" />
					<ActionItem width="77%" />
					<ActionItem width="49%" />
					<ActionItem width="33%" />
				</ShimmerBlock>
				<ShimmerBlock>
					<SectionHeader width="41%" />
					<ActionItem width="77%" />
					<ActionItem width="49%" />
					<ActionItem width="31%" />
					<ActionItem width="24%" />
					<ActionItem width="51%" />
					<ActionItem width="33%" />
				</ShimmerBlock>
			</div>
		</div>
	);
}

function Metadata({ videoPreview }: { videoPreview: JSX.Element }) {
	return (
		<div className={styles.metadata}>
			{videoPreview}
			<div className={styles.metadataContent}>
				<ShimmerBlock>
					<Shimmer height="thin" width="20%" />
					<ActionItem width="87%" />
					<ActionItem width="59%" />
					<ActionItem width="41%" />
					<ActionItem width="64%" />
					<ActionItem width="51%" />
					<ActionItem width="37%" />
					<ActionItem width="81%" />
					<ActionItem width="67%" />
					<ActionItem width="41%" />
					<ActionItem width="55%" />
					<ActionItem width="78%" />
					<ActionItem width="73%" />
				</ShimmerBlock>
			</div>
		</div>
	);
}

export default function Generating({
	children,
	isNarrow,
	className,
}: {
	children: JSX.Element;
	isNarrow: boolean;
	className?: string;
}) {
	return (
		<div
			className={cx(styles.generatingPreview, mainStyles.preview, className, { [mainStyles.forceNarrow]: isNarrow })}
		>
			{!isNarrow && <LeftPanel /> /* It's just a preview so we hide this panel rather than reorganizing the content*/}
			<Metadata videoPreview={children} />
		</div>
	);
}

export function DefaultVideoPreview({ templateType }: { templateType?: RecapTemplateType }) {
	return (
		<div className={styles.video}>
			<LoadingSpinner inline dimmer={false} className={styles.loadingSpinner} />
			<h2>{templateType ? `Generating ${TEMPLATE_TITLES[templateType]} recap…` : "Regenerating recap…"}</h2>
			<p>The Note Taker is generating a new summary for this meeting.</p>
			<small>It&apos;s safe to close this window if you need to head to your next meeting.</small>
		</div>
	);
}

export function GeneratingSummaryPreview() {
	return (
		<div className={cx(styles.video)}>
			<FontAwesomeIcon icon={faSpinner} spin className={styles.loadingSpinner} />
			<h2>Generating recap…</h2>
			<p>Please wait as the Note Taker generates a recap for this meeting.</p>
		</div>
	);
}

export function RedactingPreview({
	meetingRecording: meetingRecordingProp,
}: {
	meetingRecording: TranscriptModalGenerating_RedactingPreview_meetingRecording$key;
}) {
	const meetingRecording = useFragment(
		graphql`
			fragment TranscriptModalGenerating_RedactingPreview_meetingRecording on MeetingRecording {
				mediaType
			}
		`,
		meetingRecordingProp,
	);
	return (
		<div className={cx(styles.video)}>
			<FontAwesomeIcon icon={faSpinner} spin className={styles.loadingSpinner} />
			<h2>Processing redaction…</h2>
			<p>
				The Note Taker is redacting content from the transcript and{" "}
				{meetingRecording.mediaType === "VIDEO" ? "video" : "audio"}.
			</p>
			<small>It&apos;s safe to close this window if you need to head to your next meeting.</small>
		</div>
	);
}
